/**
 * Phone Numbers API Service (TASK-327, Epic 29/30 — Telephony)
 *
 * Thin axios wrapper over the org phone-number pool REST surface:
 *   GET   /api/v1/phone-numbers/                — pool of own organization
 *   POST  /api/v1/phone-numbers/                — request (order) a number
 *   POST  /api/v1/phone-numbers/{id}/assign/    — assign to an operator
 *   POST  /api/v1/phone-numbers/{id}/unassign/  — release from its operator
 */
import apiClient from './client';
import type {
  OrgPhoneNumber,
  RequestPhoneNumberPayload,
} from '@/types/phoneNumber';

export const phoneNumbersApi = {
  /**
   * List the organization's number pool (RBAC-scoped server-side).
   *
   * Resilient to both DRF response shapes: a bare array, or a paginated
   * object `{ count, next, previous, results: [...] }`. With global
   * pagination enabled this endpoint returns the object form, so unwrapping
   * `results` here prevents `.filter`/`.map` from throwing on the consumer
   * side (TelephonyPage table, PhonePoolField select).
   */
  list: async (): Promise<OrgPhoneNumber[]> => {
    const response = await apiClient.get('/api/v1/phone-numbers/');
    const d = response.data;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },

  /** Create a purchase request (status=requested, phone_number=""). */
  request: async (
    payload: RequestPhoneNumberPayload
  ): Promise<OrgPhoneNumber> => {
    const { data } = await apiClient.post<OrgPhoneNumber>(
      '/api/v1/phone-numbers/',
      payload
    );
    return data;
  },

  /** Assign an active pool number to an operator of the same org. */
  assign: async (id: string, userId: string): Promise<OrgPhoneNumber> => {
    const { data } = await apiClient.post<OrgPhoneNumber>(
      `/api/v1/phone-numbers/${id}/assign/`,
      { user_id: userId }
    );
    return data;
  },

  /** Release a number from its operator. */
  unassign: async (id: string): Promise<OrgPhoneNumber> => {
    const { data } = await apiClient.post<OrgPhoneNumber>(
      `/api/v1/phone-numbers/${id}/unassign/`
    );
    return data;
  },
};

export default phoneNumbersApi;
